import * as ko from 'knockout';
import { sortBy } from 'lodash';

import { BaseTrialStep } from './base';
import { WizardController } from '../../screens/trial_wizard';
import { ScheduledVisit, TrialInputApplication } from '../../models/trial';
import { Treatment, TreatmentFactor } from '../../models/treatment';
import { translate } from '../../i18n_text';
import { openInputApplicationsEdit } from './trial_input_applications_edit';
import { buildPageOptions, filterByName, visibleSlice } from './trial_applications_paging';
import { describeTreatmentComposition, sortFactorsByTestSubjects } from './treatment_composition';
import { logError } from '../../error_logging';
import i18n from '../../i18n';

let template =
  require('raw-loader!../../../templates/components/trial_wizard/trial_applications.html').default;

const PAGE_SIZE = 50;

function describeTreatment(treatment: Treatment, factors: TreatmentFactor[]): string {
  const name = translate(treatment.nameJson());
  const composition = describeTreatmentComposition(factors);

  return composition && composition !== name ? `${name} (${composition})` : name;
}

class TrialApplications extends BaseTrialStep {
  // the step only exists for treatment trials, so its number is fixed
  title = i18n.t('Step 9 - Applications')();

  loadingLibrary = ko.observable(false);

  addTooltip = i18n.t('Add application')();

  // What the input is bound to: it must update on every keystroke, or the
  // floating label reads it as empty and sits on top of the typed text. The
  // throttling belongs on the value the table filters by, not on the field.
  treatmentNameFilter = ko.observable('');
  private throttledTreatmentNameFilter = ko
    .pureComputed(() => this.treatmentNameFilter())
    .extend({ throttle: 300 });
  currentPage = ko.observable(0);

  private subscriptions: KnockoutSubscription[] = [];

  constructor(params: { controller: WizardController }) {
    super(params);
    this.ready(true);
    this.subscriptions.push(this.resetPageOnFilterChange());
  }

  private resetPageOnFilterChange(): KnockoutSubscription {
    return this.throttledTreatmentNameFilter.subscribe(() => {
      // the filter is global, so the page list changes
      this.currentPage(0);
    });
  }

  columns = ko.pureComputed<ScheduledVisit[]>(() => {
    // Hidden visits are skipped.
    // The applications themselves are kept in the payload,
    // so un-hiding the visit brings its column back with its cells intact.
    return this.trialWizard()
      .scheduledVisits()
      .filter((sv) => !sv.disabled());
  });

  private applicableTreatments = ko.pureComputed<Treatment[]>(() => {
    const treatments = this.trialWizard().treatments();
    // In a draft, plot generation drops the disabled treatments (see
    // TrialWizard.getTreatmentsForPlotGeneration), so they end up with no plots
    // and nothing to spray. Outside a draft their plots already exist and still
    // need spraying, so the rows stay.
    if (!this.trialWizard().trial().isDraft()) {
      return treatments;
    }

    return treatments.filter((treatment) => !treatment.disabled());
  });

  filteredTreatments = ko.pureComputed<Treatment[]>(() => {
    // same row order as the treatments step, whatever the payload order
    const sortedTreatments = sortBy(this.applicableTreatments(), (treatment) => treatment.order());

    return filterByName(
      sortedTreatments,
      (treatment) => translate(treatment.nameJson()),
      this.throttledTreatmentNameFilter()
    );
  });

  // Bug 3: the filter cannot be "invalid", but a filter that matches nothing
  // left only the table headers on screen with no explanation.
  noTreatmentsMatchFilter = ko.pureComputed(
    () => this.applicableTreatments().length > 0 && this.filteredTreatments().length === 0
  );

  pageOptions = ko.pureComputed(() => {
    return buildPageOptions(this.filteredTreatments().length, PAGE_SIZE);
  });

  visibleTreatments = ko.pureComputed<Treatment[]>(() => {
    return visibleSlice(this.filteredTreatments(), this.currentPage(), PAGE_SIZE);
  });

  showPagination = ko.pureComputed(() => this.pageOptions().length > 1);

  // indexed by (treatment, scheduled visit): the template reads cells
  // rows x columns times per render, so a linear scan per cell doesn't scale
  // to large treatment counts
  private applicationsByCell = ko.pureComputed(() => {
    const index = new Map<Treatment, Map<ScheduledVisit, TrialInputApplication[]>>();
    for (const application of this.trialWizard().inputApplications()) {
      let byScheduledVisit = index.get(application.treatment);
      if (!byScheduledVisit) {
        byScheduledVisit = new Map();
        index.set(application.treatment, byScheduledVisit);
      }
      let cell = byScheduledVisit.get(application.scheduledVisit);
      if (!cell) {
        cell = [];
        byScheduledVisit.set(application.scheduledVisit, cell);
      }
      cell.push(application);
    }

    return index;
  });

  cellApplications(treatment: Treatment, column: ScheduledVisit): TrialInputApplication[] {
    return this.applicationsByCell().get(treatment)?.get(column) ?? [];
  }

  cellSummaries = (treatment: Treatment, column: ScheduledVisit): string[] => {
    // one entry per product, with its concentration and application method:
    // the cell has to convey the whole plan, and the template puts each on its
    // own line so nothing has to be clipped
    return this.cellApplications(treatment, column).map((application) => application.summary());
  };

  openCell = async (treatment: Treatment, column: ScheduledVisit): Promise<void> => {
    if (!this.allowEditAny()) {
      return;
    }

    const wizard = this.trialWizard();
    const applications = this.cellApplications(treatment, column);
    const treatmentFactors = sortFactorsByTestSubjects(
      treatment.factors(),
      wizard.testSubjectsWithDimensionMeta()
    );
    try {
      const result = await openInputApplicationsEdit({
        title: `${describeTreatment(treatment, treatmentFactors)} - ${translate(column.nameJson())}`,
        applications,
        otherApplications: wizard
          .inputApplications()
          .filter((application) => applications.indexOf(application) < 0),
        scheduledVisit: column,
        treatment,
        treatmentFactors,
        isDraft: wizard.trial().isDraft(),
      });
      wizard.inputApplications(
        wizard
          .inputApplications()
          .filter((application) => applications.indexOf(application) < 0)
          .concat(result.applications)
      );
    } catch (error) {
      // dismissing rejects with no value (see InputApplicationsEdit.onCancel);
      // anything else is a real failure and must not look like a cancel
      if (error) {
        logError('input applications edit', error);
      }
    }
  };

  async reload() {
    super.reload();

    this.dispose();
    this.subscriptions.push(
      this.trialWizard.subscribe(() => this.reload()),
      // dispose() above dropped the constructor's subscription too
      this.resetPageOnFilterChange()
    );

    if (this.trialWizard().editMode === 'library') {
      this.loadingLibrary(true);
      await this.trialWizard().loadLibrarySV();
      this.loadingLibrary(false);
    }
  }

  dispose() {
    this.subscriptions.forEach((subscription) => subscription.dispose());
    this.subscriptions = [];
  }

  hasErrors() {
    return false;
  }

  showErrors() {}

  applyLocalValidation() {
    return true;
  }
}

export const trialApplications = {
  name: 'trial-applications',
  viewModel: TrialApplications,
  template: template,
};

ko.components.register(trialApplications.name, trialApplications);
